/**
 * Confirming a document that nothing was read from.
 *
 * "Confirm whole document" is an assertion: this document is right, learn from it, send it. On dev a
 * Credit Memo whose two fields were both empty took that click without a word and exported, and the
 * customer's ledger received a credit memo with no number, no date and no amount - off a page that
 * plainly carried all three.
 *
 * The required-field gate does not catch it. Cortexa proposes a type's fields, and a proposed field
 * is not required, so a document where every single value is empty clears a gate that is only ever
 * asked about the fields somebody marked. Asserting that nothing is the right answer is the one case
 * where a person can teach the product something untrue in a single click, so it is refused and both
 * ways out are named: read the document again, or fill in its values by hand (Dustin, 2026-10-13).
 */

/** Whichever parts of a document this question needs. */
export interface ConfirmableDocument {
  readonly fields: readonly {
    readonly name: string;
    readonly value: string;
    readonly canonicalValue?: string | null;
  }[];
  readonly lineItems: readonly unknown[];
}

/** Whichever parts of a job's document type this question needs. */
export interface ConfirmableType {
  readonly fields: readonly { readonly name: string }[];
}

/** The type names itself in its own field list; a type name is not something read off the page. */
const TYPE_FIELD = 'Document type';

/** What the person is told, and what to do instead. */
export const NOTHING_TO_CONFIRM =
  'Nothing was read from this document, so there is nothing to confirm. Read it again, or fill in its values below.';

/**
 * Whether confirming this document would assert that nothing is the right answer.
 *
 * False when the type asks for nothing - a type with no fields of its own is confirmed for its type
 * and its boundaries, which are answers in themselves - and false the moment one value or one table
 * row is present, because a partly read document is an ordinary review.
 */
export function confirmingNothing(
  document: ConfirmableDocument,
  type: ConfirmableType | undefined,
): boolean {
  if (!type || !type.fields.some((field) => field.name !== TYPE_FIELD)) return false;
  if (document.lineItems.length > 0) return false;
  return !document.fields.some(
    (field) =>
      field.name !== TYPE_FIELD && ((field.canonicalValue ?? field.value) || '').trim().length > 0,
  );
}
